import { env } from "@/lib/env";
import type { ApiErrorShape } from "./types";

/**
 * The single fetch entry-point for the marketing site. Every endpoint
 * function in `endpoints.ts` calls `request()` here — components must
 * never reach for `fetch` directly. This gives us:
 *
 *   - one place to attach auth, tracing, retries
 *   - one error envelope (`ApiError`) for every form
 *   - cancellation via AbortController
 *   - a single dev-mock switch (env.useMockApi)
 *
 * The mock is loaded lazily so it tree-shakes out of the prod bundle.
 */

export class ApiError extends Error implements ApiErrorShape {
  status: number;
  code: string;
  details?: unknown;
  constructor(shape: ApiErrorShape) {
    super(shape.message);
    this.name = "ApiError";
    this.status = shape.status;
    this.code = shape.code;
    this.details = shape.details;
  }
}

type Method = "GET" | "POST";

type RequestOpts<TIn> = {
  method?: Method;
  body?: TIn;
  signal?: AbortSignal;
  headers?: Record<string, string>;
};

type Interceptor = {
  onRequest?: (info: { path: string; method: Method; body?: unknown }) => void;
  onResponse?: (info: { path: string; status: number; ok: boolean }) => void;
  onError?: (info: { path: string; error: ApiError }) => void;
};

const interceptors: Interceptor[] = [];

export function registerInterceptor(i: Interceptor): () => void {
  interceptors.push(i);
  return () => {
    const idx = interceptors.indexOf(i);
    if (idx >= 0) interceptors.splice(idx, 1);
  };
}

function fire<K extends keyof Interceptor>(
  hook: K,
  payload: Parameters<NonNullable<Interceptor[K]>>[0],
) {
  for (const i of interceptors) {
    const fn = i[hook];
    if (fn) {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (fn as any)(payload);
      } catch {
        /* interceptors must never break the request flow */
      }
    }
  }
}

export async function request<TOut, TIn = unknown>(
  path: string,
  opts: RequestOpts<TIn> = {},
): Promise<TOut> {
  const method: Method = opts.method ?? (opts.body ? "POST" : "GET");

  fire("onRequest", { path, method, body: opts.body });

  if (env.useMockApi) {
    /* Dev path — never hits the network. */
    const { mockHandle } = await import("./mock-server");
    try {
      const out = await mockHandle<TOut>(path, opts.body, opts.signal);
      fire("onResponse", { path, status: 200, ok: true });
      return out;
    } catch (err) {
      const apiErr =
        err instanceof ApiError
          ? err
          : new ApiError({
              status: 500,
              code: "mock_error",
              message: err instanceof Error ? err.message : "Mock failure",
            });
      fire("onError", { path, error: apiErr });
      throw apiErr;
    }
  }

  const pathCandidates = resolvePathCandidates(path);
  const headers: Record<string, string> = {
    "content-type": "application/json",
    accept: "application/json",
    ...(env.apiKey ? { "x-api-key": env.apiKey } : {}),
    ...opts.headers,
  };

  for (let i = 0; i < pathCandidates.length; i += 1) {
    const candidatePath = pathCandidates[i];
    const isLastCandidate = i === pathCandidates.length - 1;
    const url = joinUrl(env.apiBaseUrl, candidatePath);

    let res: Response;
    try {
      res = await fetch(url, {
        method,
        headers,
        body: opts.body ? JSON.stringify(opts.body) : undefined,
        signal: opts.signal,
        mode: "cors",
        credentials: "omit",
      });
    } catch (err) {
      if (err instanceof Error && err.name === "AbortError") {
        const apiErr = new ApiError({
          status: 0,
          code: "network_error",
          message: "Request cancelled",
        });
        fire("onError", { path: candidatePath, error: apiErr });
        throw apiErr;
      }

      if (!isLastCandidate) {
        continue;
      }

      const apiErr = new ApiError({
        status: 0,
        code: "network_error",
        message: "Couldn’t reach the server. Check your connection and try again.",
      });
      fire("onError", { path: candidatePath, error: apiErr });
      throw apiErr;
    }

    fire("onResponse", {
      path: candidatePath,
      status: res.status,
      ok: res.ok,
    });

    if (res.ok) {
      return (await safeJson(res)) as TOut;
    }

    if (res.status === 404 && !isLastCandidate) {
      continue;
    }

    const body = await safeJson(res);
    const apiErr = new ApiError({
      status: res.status,
      code: body?.code ?? `http_${res.status}`,
      message:
        body?.message ?? "Something went wrong. Please try again in a moment.",
      details: body?.details,
    });
    fire("onError", { path: candidatePath, error: apiErr });
    throw apiErr;
  }

  const apiErr = new ApiError({
    status: 404,
    code: "http_404",
    message: "Couldn’t find the requested API endpoint.",
  });
  fire("onError", { path, error: apiErr });
  throw apiErr;
}

function resolvePathCandidates(path: string): string[] {
  const normalized = `/${path.replace(/^\/+/, "")}`;
  if (normalized.startsWith("/api/")) {
    return [normalized];
  }

  return [normalized, `/api${normalized}`];
}

function joinUrl(base: string, path: string): string {
  return `${base.replace(/\/+$/, "")}/${path.replace(/^\/+/, "")}`;
}

async function safeJson(
  res: Response,
): Promise<{ code?: string; message?: string; details?: unknown } | undefined> {
  const text = await res.text();
  if (!text) return undefined;
  try {
    return JSON.parse(text);
  } catch {
    return { message: text };
  }
}
